import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  PlayCircle,
  Lock,
  ChevronDown,
  ChevronUp,
  PencilRuler,
  ListChecks,
  FileText,
  ClipboardList,
} from "lucide-react";
import toast from "react-hot-toast";
import { AppContext } from "../AppContext.js";
import VideoPlayerModal from "../components/VideoPlayerModal.js";
import LessonContentModal from "../components/LessonContentModal.js";
import ShareLinkButton, { lessonLink } from "../components/ShareLinkButton.js";
import { useLearner } from "../LearnerContext.js";
import { contentFetch, recordActivity } from "../learnerApi.js";
import SmartImg from "../components/SmartImg.js";

const CourseDetailPage = () => {
  const { isLoggedIn, API_BASE_URL, isInitialized, isUserLoading } =
    useContext(AppContext);
  // Học sinh trung tâm đã mở khoá trên máy này (null: chưa). Chưa mở khoá thì máy chủ không gửi nội dung
  // (course.contentLocked), bấm vào mục nào cũng hiện popup nhập SĐT phụ huynh.
  const { learner, openGate } = useLearner();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Video đang phát: [{ title, url }] (mục có nhiều phần thì nhiều video)
  const [currentVideos, setCurrentVideos] = useState(null);
  const [expandedChapters, setExpandedChapters] = useState([]);
  // Mục đang mở dạng tài liệu hoặc bài tập: { lesson, topicTitle, mode: "document" | "exercises" }
  const [contentView, setContentView] = useState(null);
  const chapterRefs = useRef({});
  const lessonRefs = useRef({});
  // Mã mục đã mở từ link riêng, để không mở lại mỗi lần trang vẽ lại
  const openedFromLink = useRef(null);
  const navigate = useNavigate();
  const { courseId, lessonId } = useParams();

  // Helper function to safely render MathJax
  const renderMathJax = useCallback(() => {
    if (window.MathJax) {
      if (
        window.MathJax.typesetPromise &&
        typeof window.MathJax.typesetPromise === "function"
      ) {
        window.MathJax.typesetPromise().catch((err) => {
          console.warn("MathJax typeset error:", err);
        });
      } else if (window.MathJax.Hub && window.MathJax.Hub.Queue) {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub.Queue]);
      } else if (window.MathJax.typeset) {
        try {
          window.MathJax.typeset();
        } catch (err) {
          console.warn("MathJax typeset error:", err);
        }
      } else {
        setTimeout(() => {
          renderMathJax();
        }, 100);
      }
    }
  }, []);

  // Tải lại khi mở khoá / thoát học viên; chỉ hiện màn hình "Đang tải" ở lần đầu
  const loadedOnce = useRef(false);
  const fetchCourseDetail = useCallback(async () => {
    if (!loadedOnce.current) setLoading(true);
    setError(null);
    try {
      const response = await contentFetch(`${API_BASE_URL}/courses/${courseId}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Không tìm thấy khóa học này.");
        }
        throw new Error(`Lỗi HTTP! Trạng thái: ${response.status}`);
      }
      const data = await response.json();
      setCourse(data);
      loadedOnce.current = true;
    } catch (err) {
      console.error("Lỗi khi fetch chi tiết khóa học:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, API_BASE_URL, learner?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadedOnce.current = false;
  }, [courseId]);

  useEffect(() => {
    if (courseId) {
      fetchCourseDetail();
    } else {
      setLoading(false);
      setError("Không có ID khóa học để hiển thị.");
    }
  }, [courseId, fetchCourseDetail]);

  useEffect(() => {
    if (course) {
      setTimeout(() => {
        renderMathJax();
      }, 100);
    }
  }, [course, renderMathJax]);

  useEffect(() => {
    if (expandedChapters.length > 0) {
      setTimeout(() => {
        renderMathJax();
      }, 200);
    }
  }, [expandedChapters, renderMathJax]);

  // Tiêu đề để ghi việc học: "Toán 6 › Bài 1: Tập hợp… › Lý thuyết và ví dụ cơ bản"
  const trackTitle = (lesson) =>
    [course?.title, lesson.topicTitle, lesson.title].filter(Boolean).join(" › ");

  const openVideo = (lesson) => {
    setCurrentVideos({
      lesson,
      list:
        lesson.videos?.length > 0
          ? lesson.videos
          : [{ title: lesson.title, url: lesson.videoUrl }],
    });
  };

  // Đóng video / bài tập thì địa chỉ trở về trang khoá học
  const clearLessonUrl = () => {
    if (lessonId) navigate(`/course/${courseId}`, { replace: true });
  };

  const closeVideo = () => {
    setCurrentVideos(null);
    clearLessonUrl();
  };

  // Các cách xem một mục, theo thứ tự nút hiển thị: tài liệu, video, bài tập.
  // Mục kiểu cũ (chỉ có video) vẫn chỉ có nút xem video.
  // (bản bị khoá không có nội dung, chỉ có hasDocument / videoCount / exerciseCount)
  const lessonModes = (lesson) => {
    const modes = [];
    if (lesson.document || lesson.hasDocument) modes.push("document");
    if (lesson.videoUrl || lesson.videoCount > 0) modes.push("video");
    if (lesson.exercises?.length > 0 || lesson.exerciseCount > 0)
      modes.push("exercises");
    return modes.length > 0 ? modes : ["video"];
  };

  // Mở mục theo cách đã chọn; không chọn thì mở cách đầu tiên (tài liệu nếu có)
  const openLesson = (lesson, mode = lessonModes(lesson)[0]) => {
    if (mode === "video") {
      openVideo(lesson);
    } else {
      setContentView({ lesson, topicTitle: lesson.topicTitle, mode });
      recordActivity({
        type: mode === "document" ? "open_document" : "open_exercises",
        refKey: `course:${course.id}:${lesson.id}`,
        title: trackTitle(lesson),
      });
    }
  };

  // Chưa mở khoá: hiện popup nhập SĐT phụ huynh; mở khoá xong thì tải lại khoá học và mở đúng mục đó
  const askToUnlock = (itemId) => {
    openGate(() => {
      openedFromLink.current = null;
      if (itemId !== lessonId) {
        navigate(`/course/${courseId}/${itemId}`, { replace: true });
      }
    });
  };

  const handleLessonClick = (lesson, mode) => {
    // Prevent interaction if lesson is being updated
    if (lesson.updating) {
      return;
    }

    if (course.contentLocked) {
      askToUnlock(lesson.id);
      return;
    }

    // Thanh địa chỉ hiện link riêng của mục đang mở
    openedFromLink.current = lesson.id;
    if (lesson.id !== lessonId) {
      navigate(`/course/${courseId}/${lesson.id}`, { replace: true });
    }

    openLesson(lesson, mode);
  };

  // Mục ôn tập kiểm tra (kind "exam"): không xổ xuống, bấm vào mở đề cương ôn tập
  const hasExamContent = (chapter) =>
    chapter.exercises?.length > 0 || chapter.exerciseCount > 0;
  const openExam = (chapter) => {
    if (!hasExamContent(chapter)) {
      toast("Đề cương ôn tập đang được cập nhật.");
      return;
    }
    if (course.contentLocked) {
      askToUnlock(chapter.id);
      return;
    }
    recordActivity({
      type: "open_exam_outline",
      refKey: `course:${course.id}:${chapter.id}`,
      title: `${course.title} › ${chapter.title}`,
    });
    openedFromLink.current = chapter.id;
    if (chapter.id !== lessonId) {
      navigate(`/course/${courseId}/${chapter.id}`, { replace: true });
    }
    setContentView({
      lesson: {
        id: chapter.id,
        title: chapter.title,
        intro: chapter.intro,
        exercises: chapter.exercises,
      },
      topicTitle: `${course.title} · Đề cương ôn tập`,
      mode: "exercises",
      tracking: {
        refKey: `exam:${course.id}:${chapter.id}:`,
        title: `${course.title} › ${chapter.title}`,
      },
    });
  };

  // Mở link riêng /course/<khoá>/<mục>: mở chương chứa mục, cuộn tới và mở luôn mục đó.
  // Chờ có dữ liệu người dùng để biết bài có phí đã được mua hay chưa.
  const handleLessonClickRef = useRef(null);
  handleLessonClickRef.current = handleLessonClick;
  const openExamRef = useRef(null);
  openExamRef.current = openExam;
  useEffect(() => {
    if (!course || !lessonId || !isInitialized || isUserLoading) return;
    // Vừa mở khoá, khoá học đang tải lại bản đầy đủ: chờ
    if (course.contentLocked && learner) return;
    if (openedFromLink.current === lessonId) return;
    openedFromLink.current = lessonId;
    for (const chapter of course.lectures || []) {
      if (chapter.kind === "exam" && chapter.id === lessonId) {
        chapterRefs.current[chapter.id]?.scrollIntoView({ block: "center" });
        openExamRef.current(chapter);
        return;
      }
      for (const topic of chapter.topics || []) {
        const found = (topic.lessons || []).find((l) => l.id === lessonId);
        if (!found) continue;
        setExpandedChapters([chapter.id]);
        setTimeout(() => {
          lessonRefs.current[lessonId]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 300);
        if (!found.updating) {
          handleLessonClickRef.current({ ...found, topicTitle: topic.title });
        }
        return;
      }
    }
    // Link cũ trỏ tới mục đã bị xoá: về trang khoá học
    navigate(`/course/${courseId}`, { replace: true });
  }, [course, lessonId, isInitialized, isUserLoading, courseId, navigate, learner]);

  const toggleChapter = (chapterId) => {
    setExpandedChapters((prev) => {
      if (prev.includes(chapterId)) {
        return prev.filter((id) => id !== chapterId);
      } else {
        setTimeout(() => {
          if (chapterRefs.current[chapterId]) {
            chapterRefs.current[chapterId].scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
        return [chapterId];
      }
    });
  };

  const renderLessons = (lessons, topicTitle) => {
    const locked = Boolean(course.contentLocked);
    return lessons.map((rawLesson) => {
      const lesson = { ...rawLesson, topicTitle };
      // Logic for updating lessons
      const isLinked = lesson.id === lessonId;
      if (lesson.updating) {
        return (
          <div
            key={lesson.id}
            ref={(el) => (lessonRefs.current[lesson.id] = el)}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-2 mb-1 rounded-md shadow-sm transition-colors duration-200 bg-red-100 opacity-80 cursor-not-allowed lesson-row"
          >
            <span className="text-gray-700 font-medium text-sm">
              {lesson.title}
            </span>
            <button
              disabled
              className="flex items-center text-xs font-semibold sm:ml-2 flex-shrink-0 text-red-600"
            >
              <PencilRuler size={14} className="mr-1" />
              Đang cập nhật
            </button>
          </div>
        );
      }

      const modes = lessonModes(lesson);
      const onlyVideo = modes.length === 1 && modes[0] === "video";
      const MODE_BUTTONS = {
        document: [<FileText size={14} className="mr-1" />, "Tài liệu"],
        video: [
          <PlayCircle size={16} className="mr-1" />,
          onlyVideo ? "Xem" : "Video",
        ],
        exercises: [<ListChecks size={16} className="mr-1" />, "Làm bài"],
      };
      // Mỗi cách xem một nút; chưa mở khoá học viên thì nút có ổ khoá, bấm vào hiện popup mở khoá
      const buttons = modes.map((mode) => ({
        mode,
        icon: locked ? <Lock size={13} className="mr-1" /> : MODE_BUTTONS[mode][0],
        label: MODE_BUTTONS[mode][1],
      }));

      return (
        <div
          key={lesson.id}
          ref={(el) => (lessonRefs.current[lesson.id] = el)}
          className={`flex flex-col sm:flex-row sm:items-center gap-2 p-2 mb-1 rounded-md shadow-sm transition-colors duration-200 lesson-item lesson-row hover:bg-gray-100 cursor-pointer ${
            isLinked ? "ring-2 ring-indigo-400" : ""
          }`}
          onClick={() => handleLessonClick(lesson)}
        >
          {/* Điện thoại: tên mục một dòng riêng, các nút xếp hàng bên dưới; màn hình rộng: cùng một hàng */}
          <span className="text-gray-800 font-medium text-sm sm:flex-grow">
            {lesson.title}
          </span>
          <div className="flex flex-wrap items-center gap-1.5 sm:flex-nowrap sm:flex-shrink-0">
            {buttons.map((b) => (
              <button
                key={b.label}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLessonClick(lesson, b.mode);
                }}
                className={`flex items-center text-xs font-semibold view-button ${
                  locked ? "locked-button" : "text-blue-600"
                }`}
              >
                {b.icon}
                {b.label}
              </button>
            ))}
            <ShareLinkButton
              url={lessonLink(courseId, lesson.id)}
              title={`${course.title} - ${topicTitle} - ${lesson.title}`}
              className="ml-auto sm:ml-1"
            />
          </div>
        </div>
      );
    });
  };

  const renderTopics = (topics) => {
    return topics.map((topic) => (
      <div
        key={topic.id}
        className="mt-1 pl-2 sm:pl-4 border-l-2 border-purple-300 topic-container"
      >
        <div className="p-2 mb-1 font-semibold text-gray-800 topic-title">
          {topic.title}
        </div>
        {topic.lessons && topic.lessons.length > 0 && (
          <div className="lesson-container">
            {renderLessons(topic.lessons, topic.title)}
          </div>
        )}
      </div>
    ));
  };

  // Mục ôn tập kiểm tra: một dòng, bấm vào mở đề cương
  const renderExam = (chapter) => {
    const ready = hasExamContent(chapter);
    return (
      <div
        key={chapter.id}
        ref={(el) => (chapterRefs.current[chapter.id] = el)}
        className="mb-2 last:mb-0 chapter-container"
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => openExam(chapter)}
          onKeyDown={(e) => e.key === "Enter" && openExam(chapter)}
          className={`flex items-center gap-2 w-full p-2 lg:p-3 rounded-md shadow-sm transition-colors duration-200 text-gray-800 font-semibold text-base ${
            ready
              ? "bg-amber-100 hover:bg-amber-200 cursor-pointer"
              : "bg-amber-50 opacity-80 cursor-not-allowed"
          } ${chapter.id === lessonId ? "ring-2 ring-indigo-400" : ""}`}
        >
          <ClipboardList size={20} className="text-amber-600 flex-shrink-0" />
          <span className="flex-grow">{chapter.title}</span>
          {ready ? (
            <>
              <ShareLinkButton
                url={lessonLink(courseId, chapter.id)}
                title={`${course.title} - ${chapter.title}`}
              />
              <span
                className={`flex items-center text-xs font-semibold view-button flex-shrink-0 ${
                  course.contentLocked ? "locked-button" : ""
                }`}
              >
                {course.contentLocked ? (
                  <Lock size={13} className="mr-1" />
                ) : (
                  <ListChecks size={16} className="mr-1" />
                )}
                Đề cương
              </span>
            </>
          ) : (
            <span className="flex items-center text-xs font-semibold text-red-600 flex-shrink-0">
              <PencilRuler size={14} className="mr-1" /> Đang cập nhật
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderChapters = (chapters) => {
    return chapters.map((chapter) =>
      chapter.kind === "exam" ? (
        renderExam(chapter)
      ) : (
        <div
          key={chapter.id}
          ref={(el) => (chapterRefs.current[chapter.id] = el)}
          className="mb-2 last:mb-0 chapter-container"
        >
          <button
            onClick={() => toggleChapter(chapter.id)}
            className="flex items-center justify-between w-full p-2 lg:p-3 bg-purple-100 rounded-md shadow-sm hover:bg-purple-200 transition-colors duration-200 cursor-pointer text-gray-800 font-semibold text-base"
          >
            <span>{chapter.title}</span>
            {expandedChapters.includes(chapter.id) ? (
              <ChevronUp size={20} className="text-purple-600 flex-shrink-0" />
            ) : (
              <ChevronDown
                size={20}
                className="text-purple-600 flex-shrink-0"
              />
            )}
          </button>

          {expandedChapters.includes(chapter.id) &&
            chapter.topics &&
            chapter.topics.length > 0 &&
            renderTopics(chapter.topics)}
        </div>
      ),
    );
  };

  // Show loading indicator while fetching course data or user is not initialized
  if (loading && (isLoggedIn || !isInitialized)) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mx-auto mb-4"></div>
        <p className="text-xl text-gray-700">
          Đang tải chi tiết khóa học và thông tin người dùng...
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-4 py-3 rounded relative mb-4 p-8 text-center min-h-[calc(100vh-160px)] flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mx-auto mb-4"></div>
        <strong className="font-bold">Đang tải...</strong>
        <span className="block sm:inline"> Đang tải dữ liệu khóa học.</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 p-8 text-center min-h-[calc(100vh-160px)] flex flex-col items-center justify-center">
        <strong className="font-bold">Lỗi!</strong>
        <span className="block sm:inline"> {error}</span>
        <button
          onClick={() => navigate("/")}
          className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-full shadow-md transition-all duration-300"
        >
          <ArrowLeft size={18} className="inline mr-2" />
          Quay lại Trang Chủ
        </button>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-4 p-8 text-center min-h-[calc(100vh-160px)] flex flex-col items-center justify-center">
        <strong className="font-bold">Không tìm thấy!</strong>
        <span className="block sm:inline"> Không có dữ liệu khóa học.</span>
        <button
          onClick={() => navigate("/")}
          className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-full shadow-md transition-all duration-300"
        >
          <ArrowLeft size={18} className="inline mr-2" />
          Quay lại Trang Chủ
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-8 flex flex-col lg:flex-row items-start lg:space-x-10">
        {" "}
        <div className="lg:w-1/2 w-full mb-6 lg:mb-0">
          <SmartImg
            eager
            src={`/course/${course.image}`}
            fallback={`https://placehold.co/600x400/8B5CF6/FFFFFF?text=${course.title.replace(/\s/g, "+")}`}
            alt={course.title}
            className="w-full h-auto rounded-xl shadow-lg"
          />
        </div>
        <div className="lg:w-1/2 w-full">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">
            {course.title}
          </h2>{" "}
          <p className="text-gray-700 text-lg mb-6">
            {course.description} <br />
          </p>
          {course.lectures && course.lectures.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 flex items-center">
                {" "}
                <PlayCircle size={28} className="text-indigo-600 mr-3" />
                Nội dung Khóa Học
              </h3>
              <div className="bg-gray-50 rounded-lg p-2 lg:p-4 shadow-sm">
                {" "}
                {renderChapters(course.lectures)}
              </div>
            </div>
          )}
        </div>
      </div>

      <VideoPlayerModal
        videos={currentVideos?.list}
        onClose={closeVideo}
        onPlay={(video) =>
          recordActivity({
            type: "open_video",
            refKey: `course:${course.id}:${currentVideos.lesson.id}`,
            title: trackTitle(currentVideos.lesson),
            detail: currentVideos.list.length > 1 ? video.title || "" : "",
          })
        }
      />
      <LessonContentModal
        lesson={contentView?.lesson}
        topicTitle={contentView?.topicTitle}
        mode={contentView?.mode}
        tracking={
          contentView &&
          (contentView.tracking || {
            refKey: `course:${course.id}:${contentView.lesson.id}:`,
            title: trackTitle(contentView.lesson),
          })
        }
        shareUrl={contentView && lessonLink(courseId, contentView.lesson.id)}
        onOpenVideo={() => {
          openVideo(contentView.lesson);
          setContentView(null);
        }}
        onClose={() => {
          setContentView(null);
          clearLessonUrl();
        }}
      />

      <style jsx>
        {`
          .chapter-container {
            border: 1px solid #e2e8f0;
            border-radius: 0.5rem;
            margin-bottom: 1rem;
            overflow: hidden;
            box-shadow:
              0 1px 3px 0 rgba(0, 0, 0, 0.1),
              0 1px 2px 0 rgba(0, 0, 0, 0.06);
          }

          .chapter-title {
            background-color: #f8fafc;
            padding: 0.75rem 1rem;
            font-size: 1.125rem;
            color: #4a5568;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s ease-in-out;

            &:hover {
              background-color: #edf2f7;
            }
          }

          .topic-title {
            padding: 0.6rem 0.8rem;
            font-size: 1rem;
            color: #718096;
            font-weight: 600;
            border-bottom: 1px solid #edf2f7;
          }

          /* Style the lesson items */
          .lesson-item {
            padding: 0.75rem 1rem;
            font-size: 0.9rem;
            color: #2d3748;
            background-color: #ffffff;
            transition: background-color 0.2s ease-in-out;

            &:hover {
              background-color: #f7fafc;
            }
          }

          /* Indentation for visual hierarchy */
          .topic-container {
            margin-left: 2rem;
          }

          .lesson-container {
            margin-left: 3rem;
          }

          /* Styling for the "Xem" button */
          .view-button {
            background-color: #4c51bf;
            color: white;
            border-radius: 0.375rem;
            padding: 0.3rem 0.6rem;
            font-size: 0.8rem;
            transition: background-color 0.2s ease-in-out;
          }
          .view-button:hover {
            transform: scale(1.05);
            transition: transform 0.2s ease-in-out;
          }
          .view-button.locked-button {
            background-color: #e5e7eb;
            color: #4b5563;
          }
          .purchased-lesson .view-button {
            background-color: #4c51bf;
            color: white;
          }
          @media (max-width: 768px) {
            /* Điện thoại: thụt lề ít để tên mục có đủ chỗ */
            .topic-container {
              margin-left: 0.25rem;
            }

            .lesson-container {
              margin-left: 0.25rem;
            }

            .topic-title {
              padding: 0.5rem 0.25rem;
            }

            .lesson-row {
              padding: 0.6rem 0.75rem;
            }

            .view-button {
              padding: 0.4rem 0.75rem;
              font-size: 0.85rem;
            }
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .animate-fade-in {
            animation: fadeIn 0.3s ease-out forwards;
          }
          @keyframes bounce {
            0%,
            100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          .animate-bounce {
            animation: bounce 1s infinite;
          }
          @keyframes pulse {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              transform: scale(1);
            }
          }
          .animate-pulse {
            animation: pulse 1.5s infinite ease-in-out;
          }
        `}
      </style>
    </div>
  );
};

export default CourseDetailPage;
